import React from 'react';

import type { ReadPostgridContact } from '@/services/api/generated/postgridService';
import { usePostgridContactsRetrieveQuery } from '@/services/api/postgridService';

interface ContactNameProps {
  contactId: string;
  fallback?: string;
  className?: string;
}

// Helper function to get the best display name for a contact
export const getContactDisplayName = (contact: ReadPostgridContact): string => {
  // If it's a company, show company name
  if (contact.company_name?.trim()) {
    return contact.company_name.trim();
  }

  // If it's a person, show first + last name
  const firstName = contact.first_name?.trim() || '';
  const lastName = contact.last_name?.trim() || '';

  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }

  if (firstName) {
    return firstName;
  }

  if (lastName) {
    return lastName;
  }

  // Fallback to contact ID if no name is available
  return `Contact #${contact.postgrid_id || contact.id.toString().slice(-8)}`;
};

export const ContactName: React.FC<ContactNameProps> = ({
  contactId,
  fallback,
  className = '',
}) => {
  const { data: contact, isLoading } = usePostgridContactsRetrieveQuery(
    { id: contactId },
    { skip: !contactId },
  );

  if (isLoading) {
    return <span className={`text-gray-400 ${className}`}>Loading...</span>;
  }

  if (!contact) {
    return (
      <span className={`text-gray-500 ${className}`}>
        {fallback || `Contact #${contactId.slice(-8)}`}
      </span>
    );
  }

  const displayName = getContactDisplayName(contact);

  return (
    <span className={className} title={`Contact ID: ${contactId}`}>
      {displayName}
    </span>
  );
};
